import { fbt } from 'fbtee'
import { emailCategoryEnabled, type EmailCategory } from '@shared/email'
import type { NotificationPreferences } from '../../types'
import { track } from '../../lib/analytics'
import { SectionHeader } from '../../components/SectionHeader'
import { SettingsCard, SettingsRow } from '../../components/SettingsCard'
import { Switch } from '../../components/ui/switch'

const ROWS: { category: EmailCategory; label: string; description: string }[] = [
  {
    category: 'lineup',
    label: fbt('Lineup announced', 'Email settings label: emails when a festival announces its lineup'),
    description: fbt('When a festival you follow announces its lineup', 'Email settings description for lineup announcement emails'),
  },
  {
    category: 'schedule',
    label: fbt('Set times are out', 'Email settings label: emails when a festival publishes its set times'),
    description: fbt('When a festival you follow publishes its schedule', 'Email settings description for set time emails'),
  },
  {
    category: 'cancelled',
    label: fbt('Cancelled events', 'Email settings label: emails when an event is cancelled'),
    description: fbt('When an event you follow is called off', 'Email settings description for cancellation emails'),
  },
]

/** One switch per milestone email. Independent of push; who each one reaches is fixed (EMAIL_AUDIENCE). */
export function EmailSection({
  prefs,
  setEmailCategory,
}: {
  prefs: NotificationPreferences
  setEmailCategory: (category: EmailCategory, enabled: boolean) => Promise<void>
}) {
  return (
    <section>
      <SectionHeader variant="settings">
        <fbt desc="Section heading for email notification settings">Email</fbt>
      </SectionHeader>
      <SettingsCard>
        {ROWS.map(row => (
          <SettingsRow
            key={row.category}
            label={row.label}
            description={row.description}
            trailing={
              <Switch
                size="sm"
                checked={emailCategoryEnabled(prefs, row.category)}
                onCheckedChange={v =>
                  setEmailCategory(row.category, v).then(() =>
                    track('notification_settings_changed', { setting: `email_${row.category}`, enabled: v }),
                  )
                }
                className="cursor-pointer shrink-0"
              />
            }
          />
        ))}
      </SettingsCard>
      <p className="px-4 pt-2 text-xs text-muted-foreground">
        <fbt desc="Footnote under email settings: emails are skipped when the user already saw the news in the app">
          We only email you if you haven't already seen the news in the app.
        </fbt>
      </p>
    </section>
  )
}
